
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Edit2, Loader2, Ban, CheckCircle, PlusCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { createSubscriptionForUser } from '@/lib/actions/subscriptionActions';

interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  expires_at: string;
  auto_renew: boolean;
  username?: string;
  email?: string;
}

interface User {
  id: string;
  username: string | null;
  email: string | null;
}

const SubscriptionManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState<UserSubscription | null>(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editPlanId, setEditPlanId] = useState('');
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const queryClient = useQueryClient();
  
  // New state for add subscription dialog
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUsername, setSelectedUsername] = useState('');
  const [newPlanId, setNewPlanId] = useState('basic');
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);

  // Set default expiry date (30 days from now) when opening add dialog
  const setDefaultExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    setNewExpiryDate(`${year}-${month}-${day}`);
  };

  // Fetch all subscriptions with user details
  const fetchSubscriptions = async () => {
    try {
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('subscriptions')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (subscriptionsError) throw subscriptionsError;
      if (!subscriptionsData) return [];
      
      // Get user details for each subscription
      const subscriptionsWithUserDetails = await Promise.all(
        subscriptionsData.map(async (subscription) => {
          const { data: userData, error: userError } = await supabase
            .from('profiles')
            .select('username, email')
            .eq('id', subscription.user_id)
            .single();
          
          return {
            ...subscription,
            username: userData?.username || 'Unknown',
            email: userData?.email || 'Unknown'
          };
        })
      );
      
      return subscriptionsWithUserDetails;
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  };

  const { 
    data: subscriptions = [], 
    isLoading: isLoadingSubscriptions,
    refetch: refetchSubscriptions
  } = useQuery({
    queryKey: ['adminSubscriptions'],
    queryFn: fetchSubscriptions,
  });

  // Search users function
  const searchUsers = async (term: string) => {
    if (!term || term.length < 2) return;
    
    setIsSearchingUsers(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, email')
        .or(`username.ilike.%${term}%,email.ilike.%${term}%`)
        .limit(10);
      
      if (error) throw error;
      
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching users:', error);
      toast({
        title: "Fehler",
        description: "Benutzer konnten nicht gesucht werden",
        variant: "destructive",
      });
    } finally {
      setIsSearchingUsers(false);
    }
  };

  // Handle adding new subscription
  const handleAddSubscription = async () => {
    if (!selectedUserId || !newPlanId || !newExpiryDate) {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie einen Benutzer, Plan und Ablaufdatum",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    try {
      await createSubscriptionForUser(
        selectedUserId,
        newPlanId,
        new Date(newExpiryDate).toISOString()
      );
      
      toast({
        title: "Abonnement erstellt",
        description: `Abonnement für Benutzer wurde erfolgreich erstellt.`,
      });
      
      queryClient.invalidateQueries({ queryKey: ['adminSubscriptions'] });
      setIsAddDialogOpen(false);
      resetAddForm();
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      toast({
        title: "Fehler",
        description: error.message || "Abonnement konnte nicht erstellt werden",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset add subscription form
  const resetAddForm = () => {
    setSelectedUserId('');
    setSelectedUsername('');
    setNewPlanId('basic');
    setNewExpiryDate('');
    setUserSearchTerm('');
    setSearchResults([]);
  };

  // Filter subscriptions based on search term
  const filteredSubscriptions = searchTerm
    ? subscriptions.filter(sub => 
        sub.username?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        sub.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.plan_id?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : subscriptions;

  // Handle cancellation of subscription
  const handleCancelSubscription = async () => {
    if (!selectedSubscription) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({ status: 'cancelled' })
        .eq('id', selectedSubscription.id);
      
      if (error) throw error;
      
      toast({
        title: "Abonnement gekündigt",
        description: `Abonnement wurde erfolgreich gekündigt.`,
      });
      
      queryClient.invalidateQueries({ queryKey: ['adminSubscriptions'] });
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      toast({
        title: "Fehler",
        description: error.message || "Abonnement konnte nicht gekündigt werden",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsCancelDialogOpen(false);
      setSelectedSubscription(null);
    }
  };

  // Handle updating subscription details
  const handleUpdateSubscription = async () => {
    if (!selectedSubscription) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({ 
          plan_id: editPlanId,
          expires_at: new Date(editExpiryDate).toISOString(),
          // If it was cancelled before and we're updating it, set it back to active
          status: selectedSubscription.status === 'cancelled' ? 'active' : selectedSubscription.status
        })
        .eq('id', selectedSubscription.id);
      
      if (error) throw error;
      
      toast({
        title: "Abonnement aktualisiert",
        description: `Abonnement wurde erfolgreich aktualisiert.`,
      });
      
      queryClient.invalidateQueries({ queryKey: ['adminSubscriptions'] });
    } catch (error: any) {
      console.error('Error updating subscription:', error);
      toast({
        title: "Fehler",
        description: error.message || "Abonnement konnte nicht aktualisiert werden",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsEditDialogOpen(false);
      setSelectedSubscription(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getPlanName = (planId: string) => {
    const planNames: Record<string, string> = {
      'free': 'Kostenlos',
      'basic': 'Basic',
      'pro': 'Pro',
      'business': 'Business'
    };
    return planNames[planId] || planId;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  const openEditDialog = (subscription: UserSubscription) => {
    setSelectedSubscription(subscription);
    setEditPlanId(subscription.plan_id);
    // Format the date for input
    const expiryDate = new Date(subscription.expires_at);
    const year = expiryDate.getFullYear();
    const month = String(expiryDate.getMonth() + 1).padStart(2, '0');
    const day = String(expiryDate.getDate()).padStart(2, '0');
    setEditExpiryDate(`${year}-${month}-${day}`);
    setIsEditDialogOpen(true);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Abonnementverwaltung</CardTitle>
        <CardDescription>
          Benutzerabonnements anzeigen und verwalten
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              type="search"
              placeholder="Nach Benutzer oder Plan suchen..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button 
            onClick={() => {
              setDefaultExpiryDate();
              setIsAddDialogOpen(true);
            }}
            className="flex items-center gap-1"
          >
            <PlusCircle size={16} />
            Abonnement hinzufügen
          </Button>
        </div>
        
        <div className="rounded-md border">
          <div className="grid grid-cols-12 bg-gray-100 p-3 text-sm font-medium text-gray-500">
            <div className="col-span-3">Benutzer</div>
            <div className="col-span-2">Plan</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Läuft ab am</div>
            <div className="col-span-2">Automatische Verlängerung</div>
            <div className="col-span-1">Aktionen</div>
          </div>
          
          {isLoadingSubscriptions ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-2 text-gray-500">Abonnements werden geladen...</p>
            </div>
          ) : filteredSubscriptions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Keine Abonnements gefunden</p>
            </div>
          ) : (
            filteredSubscriptions.map((subscription) => (
              <div key={subscription.id} className="grid grid-cols-12 items-center border-t p-3 text-sm">
                <div className="col-span-3">
                  <div className="font-medium">{subscription.username}</div>
                  <div className="text-xs text-gray-500">{subscription.email}</div>
                </div>
                <div className="col-span-2 font-medium">
                  {getPlanName(subscription.plan_id)}
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClass(subscription.status)}`}>
                    {subscription.status === 'active' ? 'Aktiv' : 
                     subscription.status === 'cancelled' ? 'Gekündigt' : 
                     subscription.status === 'expired' ? 'Abgelaufen' : 
                     subscription.status}
                  </span>
                </div>
                <div className="col-span-2 text-gray-700">
                  {formatDate(subscription.expires_at)}
                </div>
                <div className="col-span-2 text-gray-700">
                  {subscription.auto_renew ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Ban className="h-4 w-4 text-gray-400" />
                  )}
                </div>
                <div className="col-span-1">
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-7 w-7 text-blue-600"
                      onClick={() => openEditDialog(subscription)}
                      title="Abonnement bearbeiten"
                    >
                      <Edit2 size={14} />
                    </Button>
                    {subscription.status === 'active' && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-7 w-7 text-red-600"
                        onClick={() => {
                          setSelectedSubscription(subscription);
                          setIsCancelDialogOpen(true);
                        }}
                        title="Abonnement kündigen"
                      >
                        <Ban size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abonnement kündigen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie das Abonnement von {selectedSubscription?.username} ({getPlanName(selectedSubscription?.plan_id || '')}) kündigen möchten?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelSubscription}
              className="bg-red-500 hover:bg-red-600 text-white"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird bearbeitet...
                </>
              ) : (
                'Kündigen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Subscription Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Abonnement bearbeiten</DialogTitle>
            <DialogDescription>
              Bearbeiten Sie die Details des Abonnements für {selectedSubscription?.username}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="plan" className="text-right">
                Plan
              </Label>
              <Select 
                value={editPlanId} 
                onValueChange={setEditPlanId}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Wählen Sie einen Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="expiry-date" className="text-right">
                Ablaufdatum
              </Label>
              <div className="col-span-3">
                <Input
                  id="expiry-date"
                  type="date"
                  value={editExpiryDate}
                  onChange={(e) => setEditExpiryDate(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleUpdateSubscription}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                'Speichern'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Subscription Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
        setIsAddDialogOpen(open);
        if (!open) resetAddForm();
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Neues Abonnement erstellen</DialogTitle>
            <DialogDescription>
              Erstellen Sie ein neues Abonnement für einen Benutzer
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-4">
              {!selectedUserId ? (
                <>
                  <Label>Benutzer suchen</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Benutzername oder E-Mail eingeben"
                      value={userSearchTerm}
                      onChange={(e) => {
                        setUserSearchTerm(e.target.value);
                        if (e.target.value.length > 2) {
                          searchUsers(e.target.value);
                        }
                      }}
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => searchUsers(userSearchTerm)}
                      disabled={userSearchTerm.length < 2 || isSearchingUsers}
                    >
                      {isSearchingUsers ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Suchen'}
                    </Button>
                  </div>
                  
                  {searchResults.length > 0 && (
                    <div className="border rounded-md mt-2 max-h-40 overflow-y-auto">
                      {searchResults.map(user => (
                        <div 
                          key={user.id} 
                          className="p-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                          onClick={() => {
                            setSelectedUserId(user.id);
                            setSelectedUsername(user.username || user.email || 'Unbekannter Benutzer');
                            setSearchResults([]);
                          }}
                        >
                          <div className="font-medium">{user.username || 'Kein Benutzername'}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {searchResults.length === 0 && userSearchTerm.length > 2 && !isSearchingUsers && (
                    <p className="text-sm text-gray-500">Keine Benutzer gefunden</p>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Ausgewählter Benutzer</Label>
                    <div className="font-medium">{selectedUsername}</div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setSelectedUserId('');
                      setSelectedUsername('');
                    }}
                  >
                    Ändern
                  </Button>
                </div>
              )}
            </div>
            
            {selectedUserId && (
              <>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="new-plan" className="text-right">
                    Plan
                  </Label>
                  <Select 
                    value={newPlanId} 
                    onValueChange={setNewPlanId}
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Wählen Sie einen Plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="new-expiry-date" className="text-right">
                    Ablaufdatum
                  </Label>
                  <div className="col-span-3">
                    <Input
                      id="new-expiry-date"
                      type="date"
                      value={newExpiryDate}
                      onChange={(e) => setNewExpiryDate(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleAddSubscription}
              disabled={isLoading || !selectedUserId || !newPlanId || !newExpiryDate}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Erstellen...
                </>
              ) : (
                'Abonnement erstellen'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SubscriptionManagement;
